/**
 * AdminAnalytics - Reporting & Analytics Dashboard
 * Revenue, pipeline, ticket resolution, client growth over time
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch } from './api';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(val || 0);
}

function MiniChart({ data, color, height = 60 }) {
  if (!data || data.length === 0) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5A6175', fontSize: 12 }}>No data yet</div>;

  const max = Math.max(...data.map(d => d.value), 1);
  const barWidth = Math.max(4, Math.floor(200 / data.length) - 2);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height }}>
      {data.map((d, i) => (
        <div key={i} title={`${d.label}: ${d.value}`} style={{
          width: barWidth, borderRadius: '2px 2px 0 0',
          backgroundColor: color,
          opacity: 0.3 + (d.value / max) * 0.7,
          height: Math.max(2, (d.value / max) * height),
          transition: 'height 0.3s ease',
        }} />
      ))}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminFetch(`/api/admin/analytics?period=${period}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setData({ prospects: [], clients: [], tickets: [], revenue: { total_paid: 0, total_outstanding: 0, invoice_count: 0 } }); setLoading(false); });
  }, [period]);

  const prospectChart = (data?.prospects || []).map(d => ({
    label: new Date(d.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
    value: parseInt(d.count),
  }));

  const clientChart = (data?.clients || []).map(d => ({
    label: new Date(d.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
    value: parseInt(d.count),
  }));

  const ticketChart = (data?.tickets || []).map(d => ({
    label: new Date(d.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
    value: parseInt(d.count),
  }));

  const totalProspects = prospectChart.reduce((s, d) => s + d.value, 0);
  const totalAccepted = (data?.prospects || []).reduce((s, d) => s + parseInt(d.accepted || 0), 0);
  const totalClients = clientChart.reduce((s, d) => s + d.value, 0);
  const totalTickets = ticketChart.reduce((s, d) => s + d.value, 0);
  const totalResolved = (data?.tickets || []).reduce((s, d) => s + parseInt(d.resolved || 0), 0);

  const s = {
    periodBar: { display: 'flex', gap: 8, marginBottom: 24 },
    periodBtn: (active) => ({
      padding: '8px 20px', borderRadius: 6, border: '1px solid #2A3444',
      fontSize: 13, fontWeight: 500, cursor: 'pointer',
      backgroundColor: active ? '#0057FF' : 'transparent',
      color: active ? '#FFF' : '#8B92A5',
    }),
    grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 },
    kpi: { backgroundColor: '#1A2332', borderRadius: 12, padding: 24, border: '1px solid #2A3444' },
    kpiLabel: { fontSize: 12, color: '#5A6175', marginBottom: 4, textTransform: 'uppercase', fontWeight: 600 },
    kpiValue: { fontSize: 28, fontWeight: 700, lineHeight: 1 },
    kpiSub: { fontSize: 12, marginTop: 6, color: '#5A6175' },
    chartGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 },
    chartCard: { backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', padding: 24 },
    chartTitle: { fontSize: 15, fontWeight: 600, marginBottom: 16 },
  };

  return (
    <AdminShell title="Analytics & Reporting">
      <div style={s.periodBar}>
        {['7d', '30d', '90d'].map(p => (
          <button key={p} style={s.periodBtn(period === p)} onClick={() => setPeriod(p)}>
            {p === '7d' ? 'Last 7 Days' : p === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#5A6175' }}>Loading analytics...</div>
      ) : (
        <>
          {/* KPIs */}
          <div style={s.grid}>
            <div style={s.kpi}>
              <div style={s.kpiLabel}>Revenue</div>
              <div style={{ ...s.kpiValue, color: '#00D4AA' }}>{formatCurrency(data?.revenue?.total_paid)}</div>
              <div style={s.kpiSub}>{formatCurrency(data?.revenue?.total_outstanding)} outstanding</div>
            </div>
            <div style={s.kpi}>
              <div style={s.kpiLabel}>New Prospects</div>
              <div style={{ ...s.kpiValue, color: '#5B9DFF' }}>{totalProspects}</div>
              <div style={s.kpiSub}>{totalAccepted} accepted ({totalProspects > 0 ? Math.round(totalAccepted / totalProspects * 100) : 0}%)</div>
            </div>
            <div style={s.kpi}>
              <div style={s.kpiLabel}>New Clients</div>
              <div style={{ ...s.kpiValue, color: '#10B981' }}>{totalClients}</div>
              <div style={s.kpiSub}>This period</div>
            </div>
            <div style={s.kpi}>
              <div style={s.kpiLabel}>Support Tickets</div>
              <div style={{ ...s.kpiValue, color: '#F59E0B' }}>{totalTickets}</div>
              <div style={s.kpiSub}>{totalResolved} resolved ({totalTickets > 0 ? Math.round(totalResolved / totalTickets * 100) : 0}% rate)</div>
            </div>
          </div>

          {/* Charts */}
          <div style={s.chartGrid}>
            <div style={s.chartCard}>
              <div style={s.chartTitle}>Prospect Pipeline</div>
              <MiniChart data={prospectChart} color="#5B9DFF" height={80} />
            </div>
            <div style={s.chartCard}>
              <div style={s.chartTitle}>Client Growth</div>
              <MiniChart data={clientChart} color="#10B981" height={80} />
            </div>
            <div style={s.chartCard}>
              <div style={s.chartTitle}>Ticket Volume</div>
              <MiniChart data={ticketChart} color="#F59E0B" height={80} />
            </div>
            <div style={s.chartCard}>
              <div style={s.chartTitle}>Revenue</div>
              <div style={{ padding: '16px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                  <span style={{ color: '#8B92A5', fontSize: 13 }}>Paid</span>
                  <span style={{ color: '#00D4AA', fontWeight: 600 }}>{formatCurrency(data?.revenue?.total_paid)}</span>
                </div>
                <div style={{ width: '100%', height: 8, backgroundColor: '#2A3444', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    width: data?.revenue?.total_paid > 0 ? `${Math.min(100, (data.revenue.total_paid / (data.revenue.total_paid + data.revenue.total_outstanding)) * 100)}%` : '0%',
                    height: '100%', backgroundColor: '#00D4AA', borderRadius: 4, transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                  <span style={{ color: '#8B92A5', fontSize: 13 }}>Outstanding</span>
                  <span style={{ color: '#F59E0B', fontWeight: 600 }}>{formatCurrency(data?.revenue?.total_outstanding)}</span>
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: '#5A6175' }}>{data?.revenue?.invoice_count || 0} invoices this period</div>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminShell>
  );
}
